<template>
  <div
    class="trezo-card chat-panel-enhanced mb-0 p-[8px] md:p-[10px] rounded-md flex flex-col h-full"
    :class="{
      'bot-chat': isBotChat,
      'bg-yellow-50/80 dark:bg-yellow-900/20': isBotChat
    }"
  >
    <!-- Chat Header Bar -->
    <div class="p-3 md:p-4 border-b border-gray-100 dark:border-gray-600 flex items-center justify-between">
      <div class="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
        <button 
          v-if="isMobile || isBotChat"
          @click="isBotChat ? backToCustomerChat() : backToList()"
          class="inline-block transition-all hover:text-primary-500 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 flex-shrink-0"
          :aria-label="isBotChat ? 'חזרה לשיחה' : directT('chat.backToList')"
        >
          <i class="material-symbols-outlined !text-xl"> arrow_back </i>
        </button>
        <img 
          :src="isBotChat ? currentBotImage : (chatUser.profile_picture_url || chatUser.image)"
          alt="user-image"
          class="h-8 w-8 md:h-10 md:w-10 rounded-full flex-shrink-0"
          @error="handleImageError"
        />
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-normal text-gray-900 dark:text-white truncate">
            {{ isBotChat ? 'Bot Feedback Chat' : (chatUser.name || chatUser.phone) }}
          </h3>
          <div class="flex items-center gap-1 md:gap-2">
            <span class="w-2 h-2 bg-success-500 rounded-full flex-shrink-0"></span>
            <span class="text-xs text-gray-500 dark:text-gray-400 hidden md:inline">
              {{ isBotChat ? 'Bot Response Feedback Chat' : 'Online' }}
            </span>
            <span class="text-xs text-gray-500 dark:text-gray-400 md:hidden">
              {{ isBotChat ? 'Bot Chat' : 'Online' }}
            </span>
            <span
              v-if="!isBotChat && chatUser.phone && chatUser.name && chatUser.name !== chatUser.phone"
              class="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline truncate"
            >
              • {{ chatUser.phone }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div v-if="!isBotChat" class="flex items-center gap-1 md:gap-3 flex-shrink-0">
        <!-- Bot Toggle - Desktop -->
        <div class="hidden md:flex items-center gap-2">
          <span class="text-xs text-gray-500 dark:text-gray-400">{{ directT('chat.bot') }}:</span>
          <button 
            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            :class="{
              'bg-primary-600': chatUser.botstatus === true,
              'bg-gray-300 dark:bg-gray-600': chatUser.botstatus !== true
            }"
            :title="chatUser.botstatus === true ? directT('chat.botStatus.active') : directT('chat.botStatus.inactive')"
            @click="toggleBotStatus"
          >
            <span 
              class="inline-block h-4 w-4 transform rounded-full bg-white transition"
              :class="{
                'ltr:translate-x-6 rtl:-translate-x-6': chatUser.botstatus === true,
                'ltr:translate-x-1 rtl:-translate-x-1': chatUser.botstatus !== true
              }"
            ></span>
          </button>
        </div>
        
        <!-- Bot Toggle - Mobile (Icon Only) -->
        <button 
          class="md:hidden p-1 rounded text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
          :class="{
            'text-primary-600 dark:text-primary-400': chatUser.botstatus === true
          }"
          :title="chatUser.botstatus === true ? directT('chat.botStatus.active') : directT('chat.botStatus.inactive')"
          @click="toggleBotStatus"
        >
          <i class="material-symbols-outlined text-lg">smart_toy</i>
        </button>
        
        <!-- Assignment Dropdown - Desktop -->
        <select 
          class="hidden md:block text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          :value="assignedToId || ''"
          @change="handleAssignmentChange($event)"
        >
          <option value="">{{ directT('chat.assignment.assignTo') }}</option>
          <option 
            v-for="user in orgUsers" 
            :key="user.user_id" 
            :value="user.user_id"
          >
            {{ user.display_name }}
          </option>
        </select>
        
        <!-- Assignment Button - Mobile -->
        <button 
          class="md:hidden p-1 rounded text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
          :class="{
            'text-blue-600 dark:text-blue-400': assignedToId
          }"
          :title="directT('chat.assignment.assignTo')"
        >
          <i class="material-symbols-outlined text-lg">person</i>
        </button>
        
        <!-- Chat Status Button -->
        <button 
          class="px-2 md:px-3 py-1 md:py-1.5 text-xs rounded-lg font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
          :class="{
            'bg-success-50 text-success-600 dark:bg-success-900/30 dark:text-success-400': isChatOpen,
            'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-400': !isChatOpen
          }"
          :title="isChatOpen ? directT('chat.chatStatus.open') : directT('chat.chatStatus.closed')"
          @click="toggleChatStatus"
        >
          <i class="material-symbols-outlined text-sm">
            {{ isChatOpen ? 'check' : 'close' }}
          </i>
          <span class="hidden sm:inline">{{ isChatOpen ? directT('chat.closeChat') : directT('chat.openChat') }}</span>
        </button>
        

      </div>
    </div>

    <div class="trezo-card-content flex flex-col flex-grow overflow-hidden" style="min-height: 0;">
      <div
        class="border-t border-gray-100 dark:border-[#172036] mt-[0px] mb-[0px]"
      ></div>
      <div
        class="chat-body overflow-y-auto ltr:-mr-[10px] rtl:-ml-[10px] flex flex-col"
        style="flex: 1; min-height: 0;"
      >
        <!-- Messages Area -->
        <div class="flex-1 p-4 space-y-4">
          <div
            v-for="(message, index) in currentMessages"
            :key="index"
            class="flex items-start gap-3"
            :class="{ 
              'justify-end': message.from_our_customer,
              'flex-row-reverse': !message.from_our_customer 
            }"
          >
            <!-- Avatar (always shown, position depends on message direction) -->
            <img
              :src="message.from_our_customer ? currentUserImage : (message.is_feedback ? currentBotImage : (chatUser.profile_picture_url || chatUser.image))"
              alt="user"
              class="h-8 w-8 rounded-full flex-shrink-0"
              @error="handleImageError"
            />
            
            <!-- Message Content -->
            <div :class="{ 'flex-1': message.from_our_customer }">
              <!-- Text message -->
              <div
                v-if="message.type === 'text'"
                class="rounded-lg p-3"
                :class="{
                  'bg-primary-600 text-white max-w-md ml-auto': message.from_our_customer,
                  'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 max-w-md': !message.from_our_customer
                }"
              >
                <div v-html="formatMessageText(message.text)"></div>
                <!-- Time stamp inside message bubble -->
                <p 
                  class="text-xs mt-1 opacity-70"
                  :class="{
                    'text-white/70': message.from_our_customer,
                    'text-gray-500 dark:text-gray-400': !message.from_our_customer
                  }"
                >
                  {{ formatTime(message.created_at) }}
                </p>
              </div>
              
              <!-- Photo message -->
              <div
                v-if="message.type === 'photo' || message.type === 'image'"
                class="photo-message"
                :class="{ 'max-w-xs md:max-w-md': !message.from_our_customer, 'max-w-xs md:max-w-md ml-auto': message.from_our_customer }"
              >
                <div 
                  class="photo-container cursor-pointer relative overflow-hidden rounded-lg"
                  @click="openImageInNewTab(message.link || message.media_url || message.text)"
                >
                  <img
                    :src="message.link || message.media_url || message.text"
                    :alt="directT('chat.photo')"
                    class="w-full max-w-[200px] md:max-w-xs max-h-[250px] md:max-h-[300px] object-cover rounded-lg"
                    @error="handleImageError"
                    loading="lazy"
                  />
                  <!-- Photo overlay with icon -->
                  <div class="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div class="bg-white/90 rounded-full p-2">
                      <i class="material-symbols-outlined text-gray-600">open_in_new</i>
                    </div>
                  </div>
                  <!-- Loading placeholder -->
                  <div 
                    v-if="!message.link && !message.media_url && !message.text"
                    class="flex items-center justify-center h-32 bg-gray-100 dark:bg-gray-700"
                  >
                    <div class="text-center">
                      <i class="material-symbols-outlined text-3xl text-gray-400 dark:text-gray-500 mb-2">image</i>
                      <p class="text-sm text-gray-500 dark:text-gray-400">{{ directT('chat.loadingPhoto') }}</p>
                    </div>
                  </div>
                </div>
                <!-- Photo caption if exists -->
                <p 
                  v-if="message.caption"
                  class="mt-2 p-2 rounded text-sm"
                  :class="{
                    'bg-primary-500 text-white': message.from_our_customer,
                    'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100': !message.from_our_customer
                  }"
                >
                  {{ message.caption }}
                </p>
                <!-- Time stamp for photo -->
                <p 
                  class="text-xs mt-2 opacity-70 text-center"
                  :class="{
                    'text-gray-600 dark:text-gray-400': true
                  }"
                >
                  {{ formatTime(message.created_at) }}
                </p>
              </div>
              
              <!-- Audio message -->
              <div
                v-if="message.type === 'audio' || message.type === 'voice'"
                class="audio-message"
                :class="{ 'max-w-md': !message.from_our_customer, 'max-w-md ml-auto': message.from_our_customer }"
              >
                <div 
                  class="audio-container rounded-lg p-3"
                  :class="{
                    'bg-primary-600': message.from_our_customer,
                    'bg-gray-100 dark:bg-gray-700': !message.from_our_customer
                  }"
                >
                  <!-- Audio player -->
                  <div class="flex items-center gap-3">
                    <button 
                      @click="toggleAudioPlay(message.id)"
                      class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                      :class="{
                        'bg-white/20 text-white hover:bg-white/30': message.from_our_customer,
                        'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500': !message.from_our_customer
                      }"
                    >
                      <i 
                        class="material-symbols-outlined"
                        v-if="!audioStates[message.id]?.isPlaying"
                      >
                        play_arrow
                      </i>
                      <i 
                        class="material-symbols-outlined"
                        v-else
                      >
                        pause
                      </i>
                    </button>
                    
                    <!-- Audio waveform -->
                    <div class="flex-1 flex items-center gap-1">
                      <div 
                        v-for="i in 20" 
                        :key="i"
                        class="w-1 rounded-full transition-all duration-150"
                        :class="{
                          'bg-white/70': message.from_our_customer,
                          'bg-gray-400 dark:bg-gray-500': !message.from_our_customer,
                          'bg-white': message.from_our_customer && audioStates[message.id]?.currentBar >= i,
                          'bg-gray-600 dark:bg-gray-300': !message.from_our_customer && audioStates[message.id]?.currentBar >= i
                        }"
                        :style="{ height: Math.random() * 20 + 8 + 'px' }"
                      ></div>
                    </div>
                    

                    
                    <!-- Hidden audio element -->
                    <audio 
                      :id="`audio-${message.id}`"
                      :src="message.link || message.media_url || message.text"
                      preload="metadata"
                      @loadedmetadata="onAudioLoaded(message.id, $event)"
                      @timeupdate="onAudioTimeUpdate(message.id, $event)"
                      @ended="onAudioEnded(message.id)"
                      style="display: none;"
                    ></audio>
                  </div>
                  
                  <!-- Audio duration and timestamp row -->
                  <div class="flex justify-between items-center mt-2">
                    <span 
                      class="text-xs"
                      :class="{
                        'text-white/80': message.from_our_customer,
                        'text-gray-500 dark:text-gray-400': !message.from_our_customer,
                        'order-1': message.from_our_customer,
                        'order-2': !message.from_our_customer
                      }"
                    >
                      {{ formatAudioDuration(message.duration || audioStates[message.id]?.duration || 0) }}
                    </span>
                    <span 
                      class="text-xs opacity-70"
                      :class="{
                        'text-white/70': message.from_our_customer,
                        'text-gray-500 dark:text-gray-400': !message.from_our_customer,
                        'order-2': message.from_our_customer,
                        'order-1': !message.from_our_customer
                      }"
                    >
                      {{ formatTime(message.created_at) }}
                    </span>
                  </div>
                  
                  <!-- Audio caption if exists -->
                  <p 
                    v-if="message.caption"
                    class="mt-2 text-sm"
                    :class="{
                      'text-white': message.from_our_customer,
                      'text-gray-700 dark:text-gray-300': !message.from_our_customer
                    }"
                  >
                    {{ message.caption }}
                  </p>
                  

                </div>
              </div>
              
              <!-- File message -->
              <div
                v-if="message.type === 'file' || message.type === 'document'"
                class="file-message"
                :class="{ 'max-w-md': !message.from_our_customer, 'max-w-md ml-auto': message.from_our_customer }"
              >
                <div 
                  class="file-container rounded-lg p-3"
                  :class="{
                    'bg-primary-600': message.from_our_customer,
                    'bg-gray-100 dark:bg-gray-700': !message.from_our_customer
                  }"
                >
                  <!-- File preview -->
                  <div 
                    class="flex items-center gap-3 cursor-pointer"
                    @click="downloadFile(message.media_url || message.link || message.text, message.filename)"
                  >
                    <!-- File icon based on type -->
                    <div 
                      class="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                      :class="{
                        'bg-white/20': message.from_our_customer,
                        'bg-gray-200 dark:bg-gray-600': !message.from_our_customer
                      }"
                    >
                      <i 
                        class="material-symbols-outlined text-2xl"
                        :class="{
                          'text-white': message.from_our_customer,
                          'text-gray-600 dark:text-gray-300': !message.from_our_customer
                        }"
                      >
                        {{ getFileIconFromUrl(message.media_url || message.link || message.text, message.filename) }}
                      </i>
                    </div>
                    
                    <!-- File info -->
                    <div class="flex-1 min-w-0">
                      <p 
                        class="text-sm font-medium truncate"
                        :class="{
                          'text-white': message.from_our_customer,
                          'text-gray-900 dark:text-gray-100': !message.from_our_customer
                        }"
                      >
                        {{ getFileNameFromUrl(message.media_url || message.link || message.text, message.filename) }}
                      </p>
                      <p 
                        class="text-xs opacity-70"
                        :class="{
                          'text-white/70': message.from_our_customer,
                          'text-gray-500 dark:text-gray-400': !message.from_our_customer
                        }"
                      >
                        {{ getFileTypeFromUrl(message.media_url || message.link || message.text, message.filename) }}
                        <span v-if="message.filesize"> • {{ formatFileSize(message.filesize) }}</span>
                      </p>
                    </div>
                    
                    <!-- Download icon -->
                    <div 
                      class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                      :class="{
                        'bg-white/20': message.from_our_customer,
                        'bg-gray-200 dark:bg-gray-600': !message.from_our_customer
                      }"
                    >
                      <i 
                        class="material-symbols-outlined text-sm"
                        :class="{
                          'text-white': message.from_our_customer,
                          'text-gray-600 dark:text-gray-300': !message.from_our_customer
                        }"
                      >
                        download
                      </i>
                    </div>
                  </div>
                  
                  <!-- File caption if exists -->
                  <p 
                    v-if="message.text && message.text.trim() && message.text !== (message.media_url || message.link)"
                    class="mt-2 text-sm"
                    :class="{
                      'text-white': message.from_our_customer,
                      'text-gray-700 dark:text-gray-300': !message.from_our_customer
                    }"
                  >
                    {{ message.text }}
                  </p>
                  
                  <!-- Time stamp for file -->
                  <p 
                    class="text-xs mt-2 opacity-70"
                    :class="{
                      'text-white/70': message.from_our_customer,
                      'text-gray-500 dark:text-gray-400': !message.from_our_customer,
                      'text-right': message.from_our_customer,
                      'text-left': !message.from_our_customer
                    }"
                  >
                    {{ formatTime(message.created_at) }}
                  </p>
                </div>
              </div>
              
              <!-- Bot feedback button -->
              <button
                v-if="!message.from_our_customer && !message.is_feedback"
                @click="openFeedbackModal(message)"
                class="mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                :title="directT('chat.botFeedback.addToKnowledge')"
              >
                <i class="material-symbols-outlined text-sm">psychology</i>
                {{ directT('chat.botFeedback.trainBot') }}
              </button>
                          </div>
            </div>
            

        </div>
        
        <!-- Typing Indicator (optional) -->
        <div class="flex items-center gap-2 text-gray-400 px-4 pb-2" v-if="false">
          <div class="flex space-x-1">
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
          </div>
          <span class="text-xs">{{ directT('chat.typing') || 'Typing...' }}</span>
        </div>
      </div>
      
      <!-- Chat Input Area -->
      <div class="border-t border-gray-100 dark:border-gray-600 flex flex-col">
        <!-- Main input row -->
        <div class="p-4 pb-2">
          <div class="flex items-end gap-3">
            <button 
              class="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 pb-3 relative group"
              title="צרף קבצים"
              @click="triggerFileUpload"
            >
              <i class="material-symbols-outlined">attach_file</i>
              <!-- Hidden file input -->
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                class="hidden"
                @change="handleFileUpload"
              />
            </button>
            <div class="flex-1 relative">
              <textarea
                :placeholder="isBotChat ? directT('chat.botFeedback.writeFeedbackPlaceholder') : directT('chat.writeMessage')"
                class="w-full pl-4 pr-12 py-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 resize-none h-[50px] max-h-[120px]"
                v-model="inputText"
                @submit.prevent="isBotChat ? sendBotFeedback() : sendMessage()"
                @keydown.enter.prevent="isBotChat ? sendBotFeedback() : sendMessage()"
                rows="1"
              ></textarea>
              <button 
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 emoji-button"
                @click="toggleEmojiPicker"
              >
                <i class="material-symbols-outlined">sentiment_satisfied</i>
              </button>
              
              <!-- Emoji Picker -->
              <div 
                v-if="showEmojiPicker"
                class="emoji-picker absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 w-64 z-50"
              >
                <div class="
                grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                  <button
                    v-for="emoji in emojis"
                    :key="emoji"
                    @click="addEmoji(emoji)"
                    class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-lg transition-colors"
                  >
                    {{ emoji }}
                  </button>
                </div>
              </div>
            </div>
            <button 
              @click="isBotChat ? sendBotFeedback() : sendMessage()"
              :disabled="isUploadingFiles"
              class="bg-primary-600 text-white h-[50px] px-4 rounded-lg flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed" style="margin-bottom: 0.3rem;"
            >
              <i v-if="!isUploadingFiles" class="material-symbols-outlined">send</i>
              <div v-else class="flex items-center gap-2">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span class="text-sm">מעלה...</span>
              </div>
            </button>
          </div>
        </div>
        
        <!-- Selected Files Preview -->
        <div v-if="selectedFiles.length > 0" class="p-4 pt-0">
          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ selectedFiles.length }} {{ directT('chat.filesSelected') }}
              </h4>
              <button 
                @click="selectedFiles = []"
                class="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                {{ directT('chat.clearAll') }}
              </button>
            </div>
            <div class="space-y-2">
              <div 
                v-for="(file, index) in selectedFiles" 
                :key="index"
                class="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div class="flex items-center gap-2">
                  <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <i class="material-symbols-outlined text-blue-600 dark:text-blue-400 text-sm">
                      {{ getFileIcon(file.type) }}
                    </i>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-40">
                      {{ file.name }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ formatFileSize(file.size) }}
                    </p>
                  </div>
                </div>
                <button 
                  @click="removeFile(index)"
                  class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <i class="material-symbols-outlined text-sm">close</i>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Quick action buttons - sticky to bottom -->
        <div class="px-4 pt-0 mt-auto bg-gray-25 dark:bg-gray-850 hidden md:block">
          <div class="flex items-center gap-4">
            <button 
              class="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1 relative group"
              title="Coming Soon"
            >
              <i class="material-symbols-outlined text-sm">auto_awesome</i> {{ directT('chat.quickReply') || 'Quick Reply' }}
              <!-- Coming Soon Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                Coming Soon
              </div>
            </button>
            <button 
              class="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1 relative group"
              title="Coming Soon"
            >
              <i class="material-symbols-outlined text-sm">description</i> {{ directT('chat.templates') || 'Templates' }}
              <!-- Coming Soon Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                Coming Soon
              </div>
            </button>
            <button 
              class="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1 relative group"
              title="Coming Soon"
            >
              <i class="material-symbols-outlined text-sm">person_add</i> {{ directT('chat.transfer') || 'Transfer' }}
              <!-- Coming Soon Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                Coming Soon
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- מודל הוספת שאלה ותשובה למאגר -->
  <div 
    v-if="showAddToKnowledgeModal" 
    class="fixed inset-0 z-50 flex justify-center p-4 md:p-6"
    :class="{
      'items-start pt-24': isMobile.value,
      'items-center': !isMobile.value,
      'pb-4': isMobile.value
    }"
  >
    <!-- Backdrop -->
    <div 
      class="absolute inset-0 bg-black/30 backdrop-blur-sm"
      @click="closeAddToKnowledgeModal"
    ></div>
    
    <!-- Modal Content -->
    <div class="relative bg-white dark:bg-[#0c1427] rounded-2xl w-full shadow-xl transform transition-all overflow-hidden flex flex-col"
         :class="{
           'max-w-lg max-h-[80vh]': !isMobile.value,
           'max-w-full h-full': isMobile.value
         }"
         :style="modalStyle"
    >
      <!-- Header -->
      <div class="flex-shrink-0 border-b border-gray-100 dark:border-[#172036]" 
           :class="isMobile.value ? 'p-4' : 'p-6'" 
           style="background: linear-gradient(135deg, rgb(139, 92, 246), rgb(147, 51, 234));">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full overflow-hidden">
            <img :src="currentBotImage" class="w-full h-full object-cover" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-white">
              {{ directT('chat.botFeedback.addToKnowledge') }}
            </h3>
            <p class="text-sm text-white/80" v-if="!isMobile.value">
              {{ directT('chat.botFeedback.trainBotDescription') }}
            </p>
          </div>
        </div>
      </div>
      
      <!-- Body -->
      <div class="flex-1 overflow-y-auto" :class="isMobile.value ? 'p-4' : 'p-6'">
        <div class="space-y-4">
          <!-- שאלת המקור -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ directT('chat.botFeedback.originalQuestion') }}
            </label>
            <div class="bg-gray-50 dark:bg-[#172036] rounded-xl p-3 text-sm">
              {{ selectedMessage?.qtext || selectedMessage?.text }}
            </div>
          </div>

          <!-- שאלה מחדש -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ directT('chat.botFeedback.rephraseQuestion') }}
            </label>
            <textarea
              v-model="newKnowledgeQuestion"
              :rows="isMobile.value ? 3 : 2"
              class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              :placeholder="directT('chat.botFeedback.rephraseQuestionPlaceholder')"
            ></textarea>
          </div>

          <!-- תשובה חדשה -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ directT('chat.botFeedback.newAnswer') }}
            </label>
            <textarea
              v-model="newKnowledgeAnswer"
              :rows="isMobile.value ? 5 : 4"
              class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              :placeholder="directT('chat.botFeedback.newAnswerPlaceholder')"
            ></textarea>
          </div>

          <!-- קטגוריה -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ directT('chat.botFeedback.category') }}
            </label>
            <select
              v-model="newKnowledgeCategory"
              class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">{{ directT('chat.botFeedback.selectCategory') }}</option>
              <option value="general">{{ directT('chat.botFeedback.categories.general') }}</option>
              <option value="products">{{ directT('chat.botFeedback.categories.products') }}</option>
              <option value="services">{{ directT('chat.botFeedback.categories.services') }}</option>
              <option value="support">{{ directT('chat.botFeedback.categories.support') }}</option>
              <option value="billing">{{ directT('chat.botFeedback.categories.billing') }}</option>
              <option value="technical">{{ directT('chat.botFeedback.categories.technical') }}</option>
            </select>
          </div>

          <!-- הערות -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ directT('chat.botFeedback.note') }}
            </label>
            <textarea
              v-model="newKnowledgeNote"
              :rows="isMobile.value ? 3 : 2"
              class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              :placeholder="directT('chat.botFeedback.notePlaceholder')"
            ></textarea>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="flex-shrink-0 border-t border-gray-100 dark:border-[#172036] flex gap-3"
           :class="{
             'p-4 flex-col': isMobile.value,
             'p-6 justify-between': !isMobile.value
           }">
        <button 
          @click="closeAddToKnowledgeModal"
          class="text-sm font-medium rounded-xl transition-all"
          :class="isMobile.value ? 'px-6 py-3 w-full' : 'px-4 py-2'"
          style="
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: #ffffff;
            border: none;
          "
          @mouseover="$event.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'"
          @mouseout="$event.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'"
        >
          {{ directT('chat.botFeedback.cancel') }}
        </button>
        <button 
          @click="submitNewKnowledge"
          class="text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-95"
          :class="isMobile.value ? 'px-6 py-3 w-full' : 'px-4 py-2'"
        >
          {{ directT('chat.botFeedback.addToKnowledgeBase') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Bot Feedback Modal -->
  <div 
    v-if="showBotFeedbackModal" 
    class="fixed inset-0 z-50 flex items-center justify-center px-4"
  >
    <!-- Backdrop -->
    <div 
      class="absolute inset-0 bg-black/30 backdrop-blur-sm"
      @click="closeBotFeedback"
    ></div>
    
    <!-- Modal Content -->
    <div class="relative bg-white dark:bg-[#0c1427] rounded-2xl w-full max-w-lg shadow-xl transform transition-all">
      <!-- Header -->
      <div class="p-6 border-b border-gray-100 dark:border-[#172036]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full overflow-hidden">
            <img :src="currentBotImage" class="w-full h-full object-cover" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-black dark:text-white">
              {{ directT('chat.botFeedback.botFeedbackTitle') }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ directT('chat.botFeedback.helpImprove') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Message Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ directT('chat.botFeedback.selectMessage') }}
          </label>
          <div class="relative">
            <select 
              v-model="selectedBotMessage"
              class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-2.5 text-sm"
            >
              <option v-for="msg in botMessages" :key="msg.id" :value="msg.id">
                {{ truncateMessage(msg.text) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Feedback Text -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ directT('chat.botFeedback.yourFeedback') }}
          </label>
          <textarea
            v-model="botFeedbackText"
            rows="4"
            class="w-full bg-gray-50 dark:bg-[#172036] border border-gray-200 dark:border-[#172036] rounded-xl px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            :placeholder="directT('chat.botFeedback.feedbackPlaceholder')"
          ></textarea>
        </div>
        
        <!-- Rating -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ directT('chat.botFeedback.rateResponse') }}
          </label>
          <div class="flex gap-2">
            <button 
              v-for="star in 5" 
              :key="star"
              @click="botRating = star"
              class="text-2xl transition-all"
              :class="star <= botRating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'"
            >
              ★
            </button>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="p-6 border-t border-gray-100 dark:border-[#172036] flex justify-end gap-3">
        <button 
          @click="closeBotFeedback"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#172036] rounded-xl transition-all"
        >
          {{ directT('chat.botFeedback.cancel') }}
        </button>
        <button 
          @click="submitBotFeedback"
          class="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-95"
        >
          {{ directT('chat.botFeedback.submitFeedback') }}
        </button>
      </div>
    </div>
  </div>

  <!-- דרופדאון שיוך - מעל הכל בדף -->
  <Teleport to="body">
    <div 
      v-if="showAssignDropdown" 
      class="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm"
      @click="showAssignDropdown = false"
    >
      <div 
        class="assign-dropdown w-72 bg-white dark:bg-[#0c1427] shadow-2xl rounded-2xl border border-gray-200 dark:border-[#172036] overflow-hidden"
        @click.stop
      >
        <!-- Header with gradient background -->
        <div class="p-4 text-center border-b border-gray-200 dark:border-[#172036] bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
          <div class="flex items-center gap-3 mb-1">
            <div class="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <i class="material-symbols-outlined text-gray-800 dark:text-white text-lg">group</i>
            </div>
            <div>
              <div class="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400">
                {{ directT('chat.botFeedback.assignChat') }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ directT('chat.botFeedback.selectTeamMember') }}</div>
            </div>
          </div>
        </div>
        
        <!-- Currently assigned info -->
        <div v-if="assignedToName" class="px-4 py-3 border-b border-gray-200 dark:border-[#172036] bg-green-50 dark:bg-green-900/20">
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 bg-green-500 rounded-full"></div>
            <span class="text-sm text-gray-600 dark:text-gray-300">{{ directT('chat.botFeedback.currentlyAssigned') }}</span>
            <span class="font-semibold text-green-700 dark:text-green-400">{{ assignedToName }}</span>
          </div>
        </div>
        
        <!-- Users list -->
        <div class="max-h-80 overflow-y-auto py-2">
          <div 
            v-for="user in orgUsers" 
            :key="user.user_id" 
            @click="assignToUser(user.user_id)"
            class="px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer flex items-center gap-3 group"
          >
            <div class="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 dark:from-blue-500 dark:to-blue-600 flex items-center justify-center group-hover:from-blue-500 group-hover:to-blue-600 shadow-md">
              <i class="material-symbols-outlined text-gray-800 dark:text-white text-sm">person</i>
            </div>
            <div class="flex-1">
              <div class="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                {{ user.display_name }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ directT('chat.botFeedback.teamMember') }}</div>
            </div>
            <i class="material-symbols-outlined text-gray-400 group-hover:text-blue-500 opacity-0 group-hover:opacity-100">arrow_forward</i>
          </div>
          
          <!-- Unassign option -->
          <div 
            v-if="assignedToName"
            @click="unassignUser"
            class="mx-2 mt-2 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer flex items-center gap-3 group border-t border-gray-200 dark:border-[#172036] rounded-lg"
          >
            <div class="w-8 h-8 rounded-full bg-gradient-to-r from-red-400 to-red-500 flex items-center justify-center group-hover:from-red-500 group-hover:to-red-600 shadow-md">
              <i class="material-symbols-outlined text-gray-800 dark:text-white text-sm">person_remove</i>
            </div>
            <div class="flex-1">
              <div class="font-medium text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300">
                {{ directT('chat.botFeedback.unassignChat') }}
              </div>
              <div class="text-xs text-red-500 dark:text-red-400">{{ directT('chat.botFeedback.removeAssignment') }}</div>
            </div>
            <i class="material-symbols-outlined text-red-400 group-hover:text-red-500 opacity-0 group-hover:opacity-100">close</i>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { supabase } from "@/utils/supabase";
import { useUserStore } from '@/store/user';
import axios from 'axios';
import user31Image from "@/assets/images/users/user31.jpg";

// Interface for message data
interface Message {
  id: number;
  text: string;
  phone: string;
  created_at: string;
  from_our_customer: boolean;
  organization_id: string;
  type: string;
  qtext: string | null;
  link: string | undefined;
  is_feedback?: boolean;
}

// Interface for bot chat messages
interface BotMessage {
  id: number;
  text: string;
  from_user: boolean;
  created_at: string;
  type: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | string[] | TranslationObject;
};

export default defineComponent({
  name: "ChatPanel",
  props: {
    selectedChat: {
      type: Object,
      default: null,
    },
  },
  emits: ["back-to-list", "update-bot-status", "update-chat-status", "assignment-updated", "new-message-received"],
  setup(props, { emit }) {
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject | string | string[] = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || typeof result === 'string' || Array.isArray(result) || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject | string | string[] = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || typeof enResult === 'string' || Array.isArray(enResult) || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject | string | string[];
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject | string | string[];
      }
      
      if (typeof result === 'string') {
        return result;
      } else if (Array.isArray(result)) {
        return result.join(', '); // Join array elements with comma
      } else {
        return key; // Return the key if result is not a string or array
      }
    };

    // State
    const isActive = ref(false)
    const isMobile = ref(false)
    const messageText = ref('')
    const showEmojiPicker = ref(false)
    const currentEmojiCategory = ref('smileys')
    const messages = ref<Message[]>([])
    const chatUser = ref<any>({})
    const isDarkMode = ref(false)
    
    // File upload state
    const fileInput = ref<HTMLInputElement | null>(null)
    const selectedFiles = ref<File[]>([])
    const uploadProgress = ref<number[]>([])
    const isUploadingFiles = ref(false)

    // Assignment-related state
    const showAssignDropdown = ref(false)
    const orgUsers = ref<any[]>([])
    const assignedToName = ref("")
    const assignedToId = ref<string | null>(null)
    const assignButton = ref<HTMLButtonElement | null>(null)

    // חישוב מיקום הדרופדאון
    const dropdownPosition = computed(() => {
      if (!assignButton.value) return { top: '0px', left: '0px' }
      
      const rect = assignButton.value.getBoundingClientRect()
      return {
        top: `${rect.bottom + 8}px`,
        left: `${rect.right - 192}px` // 192px = w-48 (48 * 4)
      }
    })

    // Bot-related state
    const isBotChat = ref(false)
    const botFeedbackText = ref('')
    const botFeedbackMessages = ref<Message[]>([])
    const showBotFeedbackModal = ref(false)
    const selectedBotMessage = ref<number | null>(null)
    const botRating = ref(0)

    // Emoji-related data
    const emojis = [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '😍', '🥰', '😘', '😗', '😚', '😙', '😋',
      '😛', '😜', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
      '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔',
      '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵',
      '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙏', '🤝',
      '💪', '❤️', '💔', '💯', '💢', '💥', '💫', '💦', '💨', '👀',
    ]
    
    const emojiCategories = [
      { id: 'smileys', name: 'Emojis', icon: '😀' },
      { id: 'people', name: 'People', icon: '👪' },
      { id: 'animals', name: 'Animals', icon: '🐱' },
      { id: 'food', name: 'Food', icon: '🍔' },
      { id: 'travel', name: 'Travel', icon: '✈️' },
      { id: 'activities', name: 'Activities', icon: '⚽' },
      { id: 'objects', name: 'Staff', icon: '💡' },
      { id: 'symbols', name: 'Smileys', icon: '❤️' }
    ]

    const currentEmojis = computed(() => emojis)
    
    // Computed style for modal
    const modalStyle = computed(() => ({
      'margin-top': isMobile.value ? '50px' : '0px'
    }))

    // Helper functions
    const checkIfMobile = (): void => {
      isMobile.value = window.innerWidth < 1024 // lg breakpoint in Tailwind
    }

    // Check and track dark mode
    const checkDarkMode = (): void => {
      isDarkMode.value = document.documentElement.classList.contains('dark')
    }

    // Observer for dark mode changes
    const darkModeObserver = new MutationObserver(() => {
      checkDarkMode()
    })

    // Default profile image if none provided
    const defaultProfileImage = user31Image;

    // Current user profile image
    const currentUserImage = ref(defaultProfileImage);

    // טיפול בשגיאות טעינת תמונות
    const handleImageError = (event: Event): void => {
      const target = event.target as HTMLImageElement;
      if (target) {
        target.src = defaultProfileImage;
      }
    };

    // Computed properties
    const currentBotImage = computed(() => '/images/bots/sales.png')

    const botMessages = computed(() => 
      messages.value.filter(msg => !msg.from_our_customer)
    )

    // Computed property to check if chat is open
    const isChatOpen = computed(() => {
      // צ'אט פתוח/סגור לפי העמודה isopen בטבלת threads
      const isOpen = chatUser.value.isopen === true;
      console.log('isChatOpen computed:', {
        isopen: chatUser.value.isopen,
        isOpen: isOpen
      });
      return isOpen;
    })
    
    const currentMessages = computed(() => {
      // בצ'אט הרגיל מחזירים את כל ההודעות
      if (!isBotChat.value) {
        return messages.value.filter(msg => msg && typeof msg === 'object' && Object.prototype.hasOwnProperty.call(msg, 'from_our_customer'))
      }
      
      // בצ'אט הפידבק מחזירים את ההודעות המקוריות + הודעות הפידבק
      const allMessages = [
        // הודעת פתיחה של הבוט
        {
          id: -1,
          text: 'שלום! אני כאן כדי לקבל את המשוב שלך על השיחה הזו. אנא עבור על ההודעות למטה ותן לי משוב על התשובות שקיבלת. מה לדעתך היה טוב ומה ניתן לשפר?',
          from_our_customer: false,
          created_at: new Date().toISOString(),
          type: 'text',
          phone: props.selectedChat?.phone || '',
          organization_id: props.selectedChat?.organization_id || '',
          qtext: null,
          link: undefined,
          is_feedback: true
        },
        // ההודעות המקוריות
        ...messages.value,
        // הודעות הפידבק החדשות (אם יש)
        ...botFeedbackMessages.value
      ]
      
      return allMessages.filter(msg => msg && typeof msg === 'object' && Object.prototype.hasOwnProperty.call(msg, 'from_our_customer'))
    })

    const inputText = computed({
      get: () => isBotChat.value ? botFeedbackText.value : messageText.value,
      set: (value) => {
        if (isBotChat.value) {
          botFeedbackText.value = value
        } else {
          messageText.value = value
        }
      }
    })

    // Helper functions
    const truncateMessage = (text: string) => {
      return text.length > 50 ? text.substring(0, 50) + '...' : text
    }

    // Bot-related functions
    const startBotChat = () => {
      isBotChat.value = true
      botFeedbackMessages.value = []
    }

    const backToCustomerChat = () => {
      isBotChat.value = false
      botFeedbackText.value = ''
    }

    const openBotFeedback = () => {
      showBotFeedbackModal.value = true
    }

    const closeBotFeedback = () => {
      showBotFeedbackModal.value = false
      selectedBotMessage.value = null
      botFeedbackText.value = ''
      botRating.value = 0
    }

    const submitBotFeedback = async () => {
      // כאן תוסיף את הלוגיקה לשליחת הפידבק לשרת
      console.log({
        messageId: selectedBotMessage.value,
        feedback: botFeedbackText.value,
        rating: botRating.value
      })
      closeBotFeedback()
    }

    const sendBotFeedback = async () => {
      const textToSend = isBotChat.value ? inputText.value : botFeedbackText.value;
      
      if (!textToSend.trim()) return

      // הוספת הודעת המשתמש לפידבק
      botFeedbackMessages.value.push({
        id: Date.now(),
        text: textToSend,
        from_our_customer: true,
        created_at: new Date().toISOString(),
        type: 'text',
        phone: props.selectedChat?.phone || '',
        organization_id: props.selectedChat?.organization_id || '',
        qtext: null,
        link: undefined,
        is_feedback: true
      })

      // TODO: Send feedback to server
      console.log('Sending bot feedback:', {
        threadId: props.selectedChat?.id,
        feedback: textToSend
      })

      // הוספת תגובת הבוט לפידבק
      setTimeout(() => {
        botFeedbackMessages.value.push({
          id: Date.now() + 1,
          text: 'תודה רבה על המשוב המפורט! אני אשתדל להשתפר בהתאם להערות שלך.',
          from_our_customer: false,
          created_at: new Date().toISOString(),
          type: 'text',
          phone: props.selectedChat?.phone || '',
          organization_id: props.selectedChat?.organization_id || '',
          qtext: null,
          link: undefined,
          is_feedback: true
        })
      }, 1000)

      // ניקוי השדה המתאים
      if (isBotChat.value) {
        inputText.value = '';
      } else {
        botFeedbackText.value = '';
      }
    }
    
    // פונקציית חזרה לרשימה
    const backToList = (): void => {
      emit("back-to-list");
    };
    
    // פונקציית הפעלת/כיבוי
    const toggleActive = (): void => {
      isActive.value = !isActive.value;
    };
    
    // טעינת משתמשים מהארגון
    const fetchOrgUsers = async () => {
      console.log('fetchOrgUsers called');
      try {
        // קבלת המשתמש הנוכחי
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (!session) {
          console.error('No active session');
          return;
        }
        
        console.log('Session found, user ID:', session.user.id);
        
        // קבלת פרופיל המשתמש כדי לקבל את מזהה הארגון ותמונת הפרופיל
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
          
        if (profileError || !userProfile) {
          console.error('Error fetching user profile:', profileError);
          return;
        }
        
        console.log('User profile found, org_id:', userProfile.org_id);
        
        // עדכון תמונת המשתמש הנוכחי
        if (userProfile.avatar_url) {
          currentUserImage.value = userProfile.avatar_url as string;
        }
        
        // קבלת כל המשתמשים מאותו ארגון
        const { data: users, error: usersError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('org_id', userProfile.org_id);
          
        if (usersError) {
          console.error('Error fetching organization users:', usersError);
          return;
        }
        
        console.log('Organization users fetched:', users?.length || 0);
        console.log('Users data:', users);
        orgUsers.value = users || [];
      } catch (error) {
        console.error('Error fetching organization users:', error);
      }
    };
    
    // בדיקה אם השיחה מוקצית למשתמש
    const checkAssignment = async () => {
      if (!props.selectedChat) return;
      
      try {
        const { data, error } = await supabase
          .from('threads')
          .select('assign_to')
          .eq('id', props.selectedChat.id)
          .single();
          
        if (error) {
          console.error('Error checking thread assignment:', error);
          return;
        }
        
        if (data && data.assign_to) {
          assignedToId.value = data.assign_to as string;
          await getAssignedUserName();
        } else {
          assignedToId.value = null;
          assignedToName.value = "";
        }
      } catch (error) {
        console.error('Error checking assignment:', error);
      }
    };
    
    // קבלת שם המשתמש שהוקצה
    const getAssignedUserName = async () => {
      if (!assignedToId.value) return;
      
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('display_name')
          .eq('user_id', assignedToId.value)
          .single();
          
        if (error || !data) {
          console.error('Error fetching assigned user name:', error);
          return;
        }
        
        assignedToName.value = data.display_name as string;
      } catch (error) {
        console.error('Error fetching assigned user name:', error);
      }
    };
    
    // הצגה/הסתרה של הדרופדאון - פשוט כמו כפתור הבוט
    const toggleAssignDropdown = () => {
      console.log('🎯🎯🎯 TOGGLE ASSIGN DROPDOWN FUNCTION CALLED! 🎯🎯🎯');
      showAssignDropdown.value = !showAssignDropdown.value;
      console.log('🎯 showAssignDropdown new state:', showAssignDropdown.value);
      
      // טען משתמשים אם צריך
      if (showAssignDropdown.value && orgUsers.value.length === 0) {
        fetchOrgUsers();
      }
    };
    
    // הקצאת השיחה למשתמש
    const assignToUser = async (userId: string) => {
      if (!props.selectedChat) return;
      
      try {
        // הקצאת המשתמש לשיחה
        const { error } = await supabase
          .from('threads')
          .update({ assign_to: userId })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error assigning thread:', error);
          return;
        }
        
        // עדכון המידע בממשק
        assignedToId.value = userId;
        await getAssignedUserName();
        
        // שליחת אירוע לקומפוננט האב כדי לעדכן את הסיידבר
        emit('assignment-updated', {
          chatId: props.selectedChat.id,
          assignedUserId: userId,
          assignedUserName: assignedToName.value
        });
        
        // קבלת פרטי המשתמש הנוכחי
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (session) {
          // יצירת התראה חדשה בטבלת notifications
          const { error: notifError } = await supabase
            .from('notifications')
            .insert({
              userid: userId,                          // המשתמש שקיבל את ההקצאה
              timestamp: new Date().toISOString(),     // זמן יצירת ההתראה
              type: 'alert',                           // סוג התראה: alert
              content: `Chat with ${props.selectedChat.name || props.selectedChat.phone || 'a customer'} has been assigned to you.`,
              status: 'unread'                         // סטטוס התראה: לא נקראה
            });
            
          if (notifError) {
            console.error('Error creating notification:', notifError);
          } else {
            console.log('Notification created successfully');
          }
        }
        
        showAssignDropdown.value = false;
      } catch (error) {
        console.error('Error assigning user:', error);
      }
    };
    
    // ביטול הקצאה
    const unassignUser = async () => {
      if (!props.selectedChat) return;
      
      try {
        const { error } = await supabase
          .from('threads')
          .update({ assign_to: null })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error unassigning thread:', error);
          return;
        }
        
        // עדכון המידע בממשק
        assignedToId.value = null;
        assignedToName.value = "";
        
        // שליחת אירוע לקומפוננט האב כדי לעדכן את הסיידבר
        emit('assignment-updated', {
          chatId: props.selectedChat.id,
          assignedUserId: null,
          assignedUserName: ""
        });
        
        showAssignDropdown.value = false;
      } catch (error) {
        console.error('Error unassigning user:', error);
      }
    };

    // Handle assignment change from dropdown
    const handleAssignmentChange = async (event: Event) => {
      const target = event.target as HTMLSelectElement;
      const userId = target.value;
      
      if (userId) {
        await assignToUser(userId);
      } else {
        await unassignUser();
      }
    };
    
    // סגירת הדרופדאון בלחיצה מחוץ אליו
    const closeDropdownOnOutsideClick = (event: MouseEvent) => {
      if (showAssignDropdown.value && !event.target) {
        showAssignDropdown.value = false;
      }
    };
    
    // טיפול בקליקים על המסמך
    const handleDocumentClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      
      // רק אם זה לא כפתור השיוך
      if (target.classList.contains('assign-button')) {
        return; // אל תעשה כלום אם לחצו על כפתור השיוך
      }
      
      console.log('Document click detected on:', target.className);
      
      // בודק אם הקליק היה מחוץ לדרופדאון
      if (!target.closest("#dropdownToggle") && isActive.value) {
        isActive.value = false;
      }
      
      // סגירת בורר האימוג'ים אם הקליק היה מחוץ לו
      if (showEmojiPicker.value && !target.closest('.emoji-picker') && !target.closest('.emoji-button')) {
        showEmojiPicker.value = false;
      }
    };
    
    // פתיחה/סגירה של בורר האימוג'ים
    const toggleEmojiPicker = (e?: MouseEvent): void => {
      if (e) {
        e.stopPropagation();
      }
      showEmojiPicker.value = !showEmojiPicker.value;
    };
    
    // הוספת אימוג'י לטקסט ההודעה
    const addEmoji = (emoji: string): void => {
      inputText.value += emoji;
    };
    


    // פונקציה לפורמט זמן - מטפלת גם בשניות וגם בחותמות זמן
    const formatTime = (input: string | number | null): string => {
      if (input === null) return '';
      
      // אם מדובר במספר (שניות), נפרמט כ- MM:SS
      if (typeof input === 'number') {
        const minutes = Math.floor(input / 60);
        const seconds = Math.floor(input % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
      
      // אם מדובר בחותמת זמן (string), נפרמט כשעה ודקות
      if (typeof input === 'string') {
        const date = new Date(input);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      return '';
    };



    // File upload functions
    const triggerFileUpload = (): void => {
      fileInput.value?.click();
    };

    const handleFileUpload = (event: Event): void => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      
      if (files && files.length > 0) {
        selectedFiles.value = Array.from(files);
        console.log('📎 Files selected:', selectedFiles.value);
      }
    };

    const removeFile = (index: number): void => {
      selectedFiles.value.splice(index, 1);
    };

    const getFileIcon = (fileType: string): string => {
      if (fileType.startsWith('image/')) return 'image';
      if (fileType.startsWith('video/')) return 'videocam';
      if (fileType.startsWith('audio/')) return 'audiotrack';
      if (fileType.includes('pdf')) return 'picture_as_pdf';
      if (fileType.includes('word') || fileType.includes('document')) return 'description';
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'table_chart';
      if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'slideshow';
      if (fileType.includes('text')) return 'text_snippet';
      if (fileType.includes('zip') || fileType.includes('rar')) return 'archive';
      return 'insert_drive_file';
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // File display functions
    const getFileIconFromUrl = (url: string, filename?: string): string => {
      const name = filename || url.toLowerCase();
      
      if (name.includes('.pdf')) return 'picture_as_pdf';
      if (name.includes('.doc') || name.includes('.docx')) return 'description';
      if (name.includes('.xls') || name.includes('.xlsx')) return 'table_chart';
      if (name.includes('.ppt') || name.includes('.pptx')) return 'slideshow';
      if (name.includes('.zip') || name.includes('.rar')) return 'archive';
      if (name.includes('.txt')) return 'text_snippet';
      if (name.includes('.jpg') || name.includes('.jpeg') || name.includes('.png') || name.includes('.gif')) return 'image';
      if (name.includes('.mp4') || name.includes('.avi') || name.includes('.mov')) return 'video_file';
      if (name.includes('.mp3') || name.includes('.wav') || name.includes('.m4a')) return 'audio_file';
      return 'insert_drive_file';
    };

    const getFileNameFromUrl = (url: string, filename?: string): string => {
      if (filename) return filename;
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || directT('chat.file') || 'קובץ';
    };

    const getFileTypeFromUrl = (url: string, filename?: string): string => {
      const name = filename || url.toLowerCase();
      
      if (name.includes('.pdf')) return 'PDF';
      if (name.includes('.doc') || name.includes('.docx')) return 'Word';
      if (name.includes('.xls') || name.includes('.xlsx')) return 'Excel';
      if (name.includes('.ppt') || name.includes('.pptx')) return 'PowerPoint';
      if (name.includes('.zip') || name.includes('.rar')) return 'Archive';
      if (name.includes('.txt')) return 'Text';
      if (name.includes('.jpg') || name.includes('.jpeg') || name.includes('.png') || name.includes('.gif')) return 'Image';
      if (name.includes('.mp4') || name.includes('.avi') || name.includes('.mov')) return 'Video';
      if (name.includes('.mp3') || name.includes('.wav') || name.includes('.m4a')) return 'Audio';
      return 'File';
    };

    const downloadFile = (url: string, filename?: string): void => {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || getFileNameFromUrl(url);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const uploadFileToSupabase = async (file: File): Promise<string | null> => {
      try {
        const userStore = useUserStore();
        const organization_id = userStore.user?.organization_id || props.selectedChat?.organization_id;
        
        if (!organization_id) {
          console.error('No organization ID available');
          return null;
        }

        // Generate unique filename
        const fileExtension = file.name.split('.').pop();
        const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
        const filePath = `${organization_id}/${uniqueFileName}`;

        console.log('🔄 Uploading file to Supabase:', filePath);

        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
          .from('messages')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error('❌ Upload error:', error);
          return null;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('messages')
          .getPublicUrl(filePath);

        console.log('✅ File uploaded successfully:', urlData.publicUrl);
        return urlData.publicUrl;
      } catch (error) {
        console.error('❌ Error uploading file:', error);
        return null;
      }
    };

    // עדכון פונקציית שליחת ההודעה
    const sendMessage = async (): Promise<void> => {
      console.log('🚀 sendMessage called');
      console.log('📝 inputText:', inputText.value);
      console.log('📎 selectedFiles:', selectedFiles.value);
      console.log('💬 selectedChat:', props.selectedChat);
      
      // Check if we have text or files
      if (!inputText.value.trim() && selectedFiles.value.length === 0) {
        console.log('❌ No message or files to send');
        return;
      }
      
      if (!props.selectedChat) {
        console.log('❌ No chat selected');
        return;
      }
      
      const userStore = useUserStore();
      const organization_id = userStore.user?.organization_id || props.selectedChat.organization_id;
      
      console.log('🏢 organization_id:', organization_id);
      console.log('📱 phone:', props.selectedChat.phone);
      
      try {
        // If we have files, upload them first
        if (selectedFiles.value.length > 0) {
          isUploadingFiles.value = true;
          
          for (const file of selectedFiles.value) {
            const mediaUrl = await uploadFileToSupabase(file);
            
            if (mediaUrl) {
              // Send file message
              const filePayload = {
                organization_id,
                phone: props.selectedChat.phone,
                media_link: mediaUrl,
                ...(inputText.value.trim() && { message: inputText.value.trim() })
              };
              
              console.log('📨 Sending file message with payload:', filePayload);
              
              await axios({
                method: 'post',
                url: '/api/send-message',
                data: filePayload,
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                withCredentials: true 
              });
              
              console.log('✅ File message sent successfully');
            }
          }
          
          isUploadingFiles.value = false;
          selectedFiles.value = [];
          
          // Clear file input
          if (fileInput.value) {
            fileInput.value.value = '';
          }
        } else {
          // Send text message only
          const apiPayload = {
            organization_id,
            message: inputText.value.trim(),
            phone: props.selectedChat.phone,
            media_url: '',
            media_type: null
          };
          
          console.log('📨 Sending text message with payload:', apiPayload);
          
          const response = await axios({
            method: 'post',
            url: '/api/send-message',
            data: apiPayload,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            withCredentials: true 
          });
          
          console.log('✅ Text message sent successfully', response.data);
        }
        
        // ניקוי הטופס
        inputText.value = '';
        
        // Scroll to bottom after sending message
        setTimeout(() => {
          scrollToBottom();
        }, 100);
        
        console.log('🧹 Form cleared');
        
      } catch (err: any) {
        console.error('❌ Failed to send message:', err);
        console.error('❌ Error response:', err.response?.data);
        console.error('❌ Error status:', err.response?.status);
        alert(directT('chat.errorSending') || 'Error sending message: ' + (err.response?.data?.message || err.message));
        isUploadingFiles.value = false;
      }
    };

    // Function to scroll to bottom of chat
    const scrollToBottom = () => {
      const performScroll = () => {
        const chatBody = document.querySelector('.chat-body');
        
        if (chatBody) {
          // Force smooth scroll to bottom
          chatBody.scrollTo({
            top: chatBody.scrollHeight,
            behavior: 'smooth'
          });
          
          console.log('📜 Scrolled to bottom of chat', {
            scrollTop: chatBody.scrollTop,
            scrollHeight: chatBody.scrollHeight,
            clientHeight: chatBody.clientHeight,
            shouldScrollTo: chatBody.scrollHeight
          });
        } else {
          console.log('❌ Could not find chat-body container');
        }
      };
      
      // Immediate scroll attempt
      nextTick(() => {
        performScroll();
      });
      
      // Backup scroll attempts to handle async content loading
      setTimeout(performScroll, 100);
      setTimeout(performScroll, 300);
      setTimeout(performScroll, 500);
    };

    // Load messages from the database
    const loadMessages = async () => {
      if (!props.selectedChat?.phone || !props.selectedChat?.organization_id) {
        console.log('❌ Cannot load messages: missing chat data');
        console.log('phone:', props.selectedChat?.phone);
        console.log('organization_id:', props.selectedChat?.organization_id);
        return;
      }
      
      console.log('📨 Loading messages for:', props.selectedChat.phone);
      
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('phone', props.selectedChat.phone)
          .eq('organization_id', props.selectedChat.organization_id)
          .order('created_at', { ascending: true });
          
        if (error) {
          console.error('❌ Error loading messages:', error);
          return;
        }
        
        messages.value = (data as any[]) || [];
        console.log(`✅ Loaded ${messages.value.length} messages:`, messages.value);
        
        // Wait for Vue to update the DOM, then scroll
        await nextTick();
        
        // Wait a bit more for any images/content to load
        setTimeout(() => {
          scrollToBottom();
          console.log('📜 Scroll triggered after messages loaded');
        }, 300);
      } catch (err) {
        console.error('❌ Error loading messages:', err);
      }
    };
    
    // Set up basic lifecycle hooks
    onMounted(() => {
      console.log('🔧 Component mounted');
      document.addEventListener("click", handleDocumentClick);
      checkIfMobile();
      window.addEventListener('resize', checkIfMobile);
      
      // Initialize and track dark mode
      checkDarkMode();
      darkModeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
      
      // טעינת משתמשי הארגון
      fetchOrgUsers();
      
      // הוספת מאזין לסגירת הדרופדאון
      document.addEventListener('click', closeDropdownOnOutsideClick);
      
      // Setup initial subscription if we have a selected chat with organization_id
      if (props.selectedChat?.organization_id) {
        console.log('🔔 Setting up initial subscription on mount');
        setupMessagesSubscription();
      }
      
      // Ensure scroll to bottom on initial load
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    });
    
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener('resize', checkIfMobile);
      
      // Clean up dark mode observer
      darkModeObserver.disconnect();
      
      // Clean up the realtime subscription
      if (messagesSubscription) {
        console.log('Cleaning up realtime subscription');
        supabase.removeChannel(messagesSubscription);
        messagesSubscription = null;
        currentOrganizationId = null;
      }
      
      // הסרת מאזינים
      document.removeEventListener('click', closeDropdownOnOutsideClick);
    });

    // Use a realtime subscription to receive new messages in real-time for entire organization
    let messagesSubscription: any = null;
    let currentOrganizationId: string | null = null;

    const setupMessagesSubscription = () => {
      // Clear any existing subscription
      if (messagesSubscription) {
        console.log('🔄 Clearing existing realtime subscription');
        supabase.removeChannel(messagesSubscription);
        messagesSubscription = null;
        currentOrganizationId = null; // Reset organization ID when clearing
      }
      
      // Don't set up subscription if no organization ID available
      if (!props.selectedChat?.organization_id) {
        console.log('❌ Cannot setup subscription: missing organization data');
        return;
      }
      
      // Always setup subscription for organization (even if same org - handles reconnections)
      currentOrganizationId = props.selectedChat.organization_id;
      
      console.log('🔔 Setting up realtime subscription for ALL organization messages');
      console.log('🏢 Org ID:', props.selectedChat.organization_id);
      console.log('📡 This will listen to ALL messages in the organization, not just current chat');
      
      // Subscribe to ALL messages for this organization
      messagesSubscription = supabase
        .channel('organization-messages-changes')
        .on('postgres_changes', 
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `organization_id=eq.${props.selectedChat.organization_id}`
          }, 
          (payload) => {
            console.log('🔔 Received new message via realtime for organization:', payload);
            console.log('📱 Message phone:', payload.new?.phone);
            console.log('🏢 Message org_id:', payload.new?.organization_id);
            console.log('📝 Message text:', payload.new?.text);
            console.log('👤 From customer:', payload.new?.from_our_customer);
            
            if (payload.new) {
              const newMessage: Message = {
                id: payload.new.id || 0,
                text: payload.new.text || '',
                phone: payload.new.phone || '',
                created_at: payload.new.created_at || new Date().toISOString(),
                from_our_customer: payload.new.from_our_customer || false,
                organization_id: payload.new.organization_id || '',
                type: payload.new.type || 'text',
                qtext: payload.new.qtext || null,
                link: payload.new.link || null
              };
              
              // If this message is for the currently opened chat, add it to the UI
              if (props.selectedChat && payload.new.phone === props.selectedChat.phone) {
              messages.value = [...messages.value, newMessage];
                console.log('✅ Added new message to current chat UI. Total messages:', messages.value.length);
                // Scroll to bottom after adding new message
                scrollToBottom();
              } else {
                console.log('📱 Message is for different chat. Current chat:', props.selectedChat?.phone, 'Message chat:', payload.new.phone);
              }
              
              // Always emit to parent to update sidebar with new message info
              const emitData = {
                message: newMessage,
                phone: payload.new.phone,
                threadId: payload.new.thread_id || null
              };
              
              console.log('📡 About to emit new message to parent:', emitData);
              emit('new-message-received', emitData);
              console.log('✅ Successfully emitted new message to parent for sidebar update');
            }
          }
        )
        .subscribe((status) => {
          console.log('🔔 Organization-wide realtime subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to organization-wide messages!');
            console.log('🎯 Now listening for ALL messages in organization:', props.selectedChat?.organization_id);
          } else if (status === 'CLOSED') {
            console.log('❌ Subscription was closed');
            currentOrganizationId = null; // Reset when closed
          }
        });
    };

    // Watch for changes to the selected chat
    watch(() => props.selectedChat?.id, (newChatId) => {
      console.log('Selected chat changed to:', newChatId);
      
      // Clear messages when no chat is selected
      if (!newChatId) {
        messages.value = [];
        return;
      }
      
      // Load messages for new chat
      loadMessages();
    }, { immediate: true });
    
    // Watch for changes in selectedChat
    watch(() => props.selectedChat, async (newChat) => {
      if (newChat) {
        console.log('Selected chat updated:', newChat);
        console.log('Chat isopen:', newChat.isopen);
        console.log('Chat botstatus:', newChat.botstatus);
        console.log('Chat assign_to:', newChat.assign_to);
        
        // Clear current messages first for better UX
        messages.value = [];
        
        await loadMessages();
        chatUser.value = newChat;
        
        // בדיקה אם השיחה מוקצית למישהו
        await checkAssignment();
        
        // Ensure scroll to bottom after chat change
        setTimeout(() => {
          scrollToBottom();
        }, 200);
        
        console.log('isChatOpen computed value:', isChatOpen.value);
      }
    }, { immediate: true });

    // Watch for organization changes to setup subscription only when needed
    watch(() => props.selectedChat?.organization_id, (newOrgId, oldOrgId) => {
      console.log('Organization ID changed:', { from: oldOrgId, to: newOrgId });
      
      if (newOrgId && newOrgId !== oldOrgId) {
        console.log('🔔 New organization detected, setting up subscription');
        setupMessagesSubscription();
      }
    }, { immediate: false }); // Don't run immediately since onMounted handles initial setup

    // Watch for changes in messages array to auto-scroll
    watch(() => messages.value.length, (newLength, oldLength) => {
      if (newLength > 0 && newLength !== oldLength) {
        console.log(`📨 Messages count changed from ${oldLength} to ${newLength}, scrolling to bottom`);
        
        // Wait for DOM update, then scroll
        nextTick(() => {
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        });
      }
    });

    // Add a helper function to format message text with proper line breaks and bullet points
    const formatMessageText = (text: string): string => {
      if (!text) return '';
      
      // Escape HTML to prevent XSS
      let formattedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      
      // Convert line breaks to <br>
      formattedText = formattedText.replace(/\n/g, '<br>');
      
      // Handle bullet points (* ) by converting them to proper HTML bullets
      formattedText = formattedText.replace(/\* /g, '• ');
      
      return formattedText;
    };

    // Function to open image in a new tab
    const openImageInNewTab = (imageUrl: string | null): void => {
      if (imageUrl) {
        window.open(imageUrl, '_blank');
      }
    };

    // Audio states management
    const audioStates = ref<Record<number, {
      isPlaying: boolean;
      duration: number;
      currentTime: number;
      currentBar: number;
    }>>({});

    // Audio functions
    const toggleAudioPlay = (messageId: number): void => {
      const audioElement = document.getElementById(`audio-${messageId}`) as HTMLAudioElement;
      if (!audioElement) return;

      const state = audioStates.value[messageId];
      
      if (state?.isPlaying) {
        audioElement.pause();
        audioStates.value[messageId].isPlaying = false;
      } else {
        // Pause all other audio messages first
        Object.keys(audioStates.value).forEach(id => {
          if (parseInt(id) !== messageId && audioStates.value[parseInt(id)].isPlaying) {
            const otherAudio = document.getElementById(`audio-${id}`) as HTMLAudioElement;
            if (otherAudio) otherAudio.pause();
            audioStates.value[parseInt(id)].isPlaying = false;
          }
        });
        
        audioElement.play();
        if (!audioStates.value[messageId]) {
          audioStates.value[messageId] = {
            isPlaying: true,
            duration: 0,
            currentTime: 0,
            currentBar: 0
          };
        } else {
          audioStates.value[messageId].isPlaying = true;
        }
      }
    };

    const onAudioLoaded = (messageId: number, event: Event): void => {
      const audioElement = event.target as HTMLAudioElement;
      if (!audioStates.value[messageId]) {
        audioStates.value[messageId] = {
          isPlaying: false,
          duration: audioElement.duration || 0,
          currentTime: 0,
          currentBar: 0
        };
      } else {
        audioStates.value[messageId].duration = audioElement.duration || 0;
      }
    };

    const onAudioTimeUpdate = (messageId: number, event: Event): void => {
      const audioElement = event.target as HTMLAudioElement;
      if (audioStates.value[messageId]) {
        audioStates.value[messageId].currentTime = audioElement.currentTime;
        const progress = audioElement.currentTime / audioElement.duration;
        audioStates.value[messageId].currentBar = Math.floor(progress * 20);
      }
    };

    const onAudioEnded = (messageId: number): void => {
      if (audioStates.value[messageId]) {
        audioStates.value[messageId].isPlaying = false;
        audioStates.value[messageId].currentTime = 0;
        audioStates.value[messageId].currentBar = 0;
      }
    };

    const formatAudioDuration = (duration: number): string => {
      if (!duration || isNaN(duration)) return '0:00';
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const downloadAudio = (audioUrl: string | null): void => {
      if (audioUrl) {
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = `audio-${Date.now()}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const toggleBotStatus = async (): Promise<void> => {
      if (!props.selectedChat || !props.selectedChat.id) {
        console.log('Cannot toggle bot status: No chat selected or missing thread id');
        return;
      }
      
      const newStatus = !chatUser.value.botstatus;
      
      console.log('Toggling bot status for thread:', {
        id: props.selectedChat.id,
        currentStatus: chatUser.value.botstatus,
        newStatus: newStatus
      });
      
      try {
        const { error } = await supabase
          .from('threads')
          .update({ botstatus: newStatus })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error updating bot status:', error);
          return;
        }
        
        // Emit event to parent instead of mutating prop directly
        emit('update-bot-status', {
          threadId: props.selectedChat.id,
          botstatus: newStatus
        });
        
        console.log('Bot status updated successfully:', newStatus);
      } catch (err) {
        console.error('Failed to update bot status:', err);
      }
    };

    const toggleChatStatus = async (): Promise<void> => {
      if (!props.selectedChat || !props.selectedChat.id) {
        console.log('Cannot toggle chat status: No chat selected or missing thread id');
        return;
      }
      
      console.log('Before toggle - chatUser.value:', chatUser.value);
      console.log('Before toggle - isopen:', chatUser.value.isopen);
      console.log('Before toggle - isChatOpen:', isChatOpen.value);
      
      // הלוגיקה החדשה:
      // אם הצ'אט פתוח (isopen = true), נסגור אותו (isopen = false)
      // אם הצ'אט סגור (isopen = false), נפתח אותו (isopen = true)
      const newIsOpenStatus = !isChatOpen.value; // הפוך מהמצב הנוכחי
      
      console.log('Toggling chat status for thread:', {
        id: props.selectedChat.id,
        currentlyOpen: isChatOpen.value,
        newIsOpenStatus: newIsOpenStatus
      });
      
      try {
        const { error } = await supabase
          .from('threads')
          .update({ isopen: newIsOpenStatus })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error updating chat status:', error);
          return;
        }
        
        // עדכון המשתנה המקומי
        chatUser.value.isopen = newIsOpenStatus;
        
        console.log('After update - chatUser.value:', chatUser.value);
        console.log('After update - isChatOpen:', isChatOpen.value);
        
        // שליחת אירוע להורה
        emit('update-chat-status', {
          threadId: props.selectedChat.id,
          isopen: newIsOpenStatus
        });
        
        console.log('Chat status updated successfully:', newIsOpenStatus ? 'open' : 'closed');
      } catch (err) {
        console.error('Failed to update chat status:', err);
      }
    };

    // משתנים למודל הוספת ידע
    const showAddToKnowledgeModal = ref(false)
    const selectedMessage = ref<Message | null>(null)
    const newKnowledgeQuestion = ref('')
    const newKnowledgeAnswer = ref('')
    const newKnowledgeCategory = ref('')
    const newKnowledgeNote = ref('')

    // פתיחת מודל הוספת ידע
    const openFeedbackModal = (message: Message) => {
      selectedMessage.value = message
      newKnowledgeQuestion.value = message.qtext || message.text || ''
      newKnowledgeAnswer.value = ''
      showAddToKnowledgeModal.value = true
    }

    // סגירת המודל
    const closeAddToKnowledgeModal = () => {
      showAddToKnowledgeModal.value = false
      selectedMessage.value = null
      newKnowledgeQuestion.value = ''
      newKnowledgeAnswer.value = ''
      newKnowledgeCategory.value = ''
      newKnowledgeNote.value = ''
    }

    // שליחת הידע החדש לשרת
    const submitNewKnowledge = async () => {
      try {
        // Get current user ID and organization ID
        const userStore = useUserStore();
        const userId = userStore.user?.id;
        const orgId = userStore.user?.organization_id || props.selectedChat?.organization_id;
        
        if (!userId) {
          // TODO: Replace with createToast when available
          alert('שגיאה: לא נמצא משתמש מחובר');
          return;
        }
        
        if (!orgId) {
          // TODO: Replace with createToast when available
          alert('שגיאה: לא נמצא מזהה ארגון');
          return;
        }

        // Validate required fields
        if (!newKnowledgeQuestion.value.trim()) {
          // TODO: Replace with createToast when available
          alert('נא להזין שאלה');
          return;
        }
        
      if (!newKnowledgeAnswer.value.trim()) {
          // TODO: Replace with createToast when available
          alert('נא להזין תשובה');
          return;
        }

        // Save to database using Supabase (same as simulator)
        const { data, error } = await supabase
          .from('faq')
          .insert({
            organization_id: orgId,
            created_by: userId,
            question: newKnowledgeQuestion.value.trim(),
          answer: newKnowledgeAnswer.value.trim(),
            category: newKnowledgeCategory.value || 'general',
            note: newKnowledgeNote.value.trim() || null
          })
          .select()
          .single();

        if (error) {
          console.error('Error saving to knowledge base:', error);
          // TODO: Replace with createToast when available
          alert('שגיאה בשמירה למאגר הידע');
          return;
        }

        console.log('Successfully saved to knowledge base:', data);
        
        // Show success message
        // TODO: Replace with createToast when available
        alert('נשמר בהצלחה למאגר הידע!');
        
        closeAddToKnowledgeModal();
        
        // הוספת הודעת אישור לצ'אט הפידבק
        botFeedbackMessages.value.push({
          id: Date.now(),
          text: 'תודה! התשובה נוספה למאגר הידע שלי.',
          from_our_customer: false,
          created_at: new Date().toISOString(),
          type: 'text',
          phone: props.selectedChat?.phone || '',
          organization_id: orgId,
          qtext: null,
          link: undefined,
          is_feedback: true
        });

      } catch (error) {
        console.error('Error in submitNewKnowledge:', error);
        // TODO: Replace with createToast when available
        alert('שגיאה בשמירה למאגר הידע');
      }
    }

    return {
      isActive,
      chatUser,
      toggleActive,
      isMobile,
      backToList,
      directT,
      messageText,
      inputText,
      showEmojiPicker,
      emojis,
      emojiCategories,
      currentEmojiCategory,
      currentEmojis,
      toggleEmojiPicker,
      addEmoji,
      sendMessage,
      formatTime,
      messages,
      toggleBotStatus,
      toggleChatStatus,
      isChatOpen,
      formatMessageText,
      openImageInNewTab,
      
      // תכונות חדשות להקצאת משתמשים
      showAssignDropdown,
      orgUsers,
      assignedToName,
      assignedToId,
      toggleAssignDropdown,
      assignToUser,
      unassignUser,
      handleAssignmentChange,
      showBotFeedbackModal,
      selectedBotMessage,
      botFeedbackText,
      botRating,
      currentBotImage,
      botMessages,
      truncateMessage,
      openBotFeedback,
      closeBotFeedback,
      submitBotFeedback,
      isBotChat,
      botFeedbackMessages,
      currentMessages,
      startBotChat,
      backToCustomerChat,
      sendBotFeedback,
      showAddToKnowledgeModal,
      selectedMessage,
      newKnowledgeQuestion,
      newKnowledgeAnswer,
      newKnowledgeCategory,
      newKnowledgeNote,
      openFeedbackModal,
      closeAddToKnowledgeModal,
      submitNewKnowledge,
      handleImageError,
      currentUserImage,
      assignButton,
      dropdownPosition,
      isDarkMode,
      modalStyle,
      
      // Audio functions
      audioStates,
      toggleAudioPlay,
      onAudioLoaded,
      onAudioTimeUpdate,
      onAudioEnded,
      formatAudioDuration,
      downloadAudio,
      
      // File upload functions
      fileInput,
      selectedFiles,
      uploadProgress,
      isUploadingFiles,
      triggerFileUpload,
      handleFileUpload,
      removeFile,
      getFileIcon,
      formatFileSize,
      
      // File display functions
      getFileIconFromUrl,
      getFileNameFromUrl,
      getFileTypeFromUrl,
      downloadFile
    };
  },
});
</script>

<style scoped>
/* Modern chat container styles */
.chat-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dark .chat-container {
  background: #1f2937;
  border-color: #374151;
}

@media (min-width: 768px) {
  .chat-container {
    padding: 25px;
  }
}

/* Message styles */
p {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Modern message styles */
.bg-primary-600 {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.bg-gray-100 {
  background: #f3f4f6 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dark .bg-gray-700 {
  background: #374151 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Message hover effects */
p:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

/* Message animations */
@keyframes messageIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

li {
  animation: messageIn 0.3s ease-out forwards;
}

/* Modern message styles */
.rounded-lg {
  word-wrap: break-word;
  word-break: break-word;
}

/* Image message styles */
img[class*="max-w-full"] {
  border-radius: 12px;
  transition: transform 0.3s ease;
}

img[class*="max-w-full"]:hover {
  transform: scale(1.02);
}

/* Modern audio waveform animation */
.w-1.animate-bounce {
  animation: waveform 1.5s ease-in-out infinite;
}

@keyframes waveform {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(1.5); }
}

/* Smooth transitions for all interactive elements */
button {
  transition: all 0.2s ease-in-out;
}

button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* Custom scrollbar for messages area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Input area styling */
textarea:focus {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Avatar styling */
.h-8.w-8.rounded-full {
  border: 2px solid #e5e7eb;
}

.dark .h-8.w-8.rounded-full {
  border-color: #374151;
}

/* Photo message specific styles */
.photo-message {
  max-width: 85%;
  display: inline-block;
}

.photo-container {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.photo-container:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.dark .photo-container {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.dark .photo-container:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.photo-image {
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.photo-container:hover .photo-image {
  transform: scale(1.05);
}

.photo-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.photo-container:hover .photo-overlay {
  background: rgba(0, 0, 0, 0.1);
  opacity: 1;
}

.photo-overlay-icon {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transform: scale(0.9);
  transition: transform 0.2s ease;
}

.dark .photo-overlay-icon {
  background: rgba(31, 41, 55, 0.9);
}

.photo-container:hover .photo-overlay-icon {
  transform: scale(1);
}

.photo-caption {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.4;
}

.photo-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 128px;
  background: #f3f4f6;
  border-radius: 12px;
  text-align: center;
}

.dark .photo-loading {
  background: #374151;
}

/* Mobile optimizations for photos */
@media (max-width: 768px) {
  .photo-message {
    max-width: 90%;
  }
  
  .photo-image {
    max-height: 250px;
  }
  
  .photo-caption {
    font-size: 0.8125rem;
    padding: 6px 10px;
  }
}

/* Audio message specific styles */
.audio-message {
  max-width: 85%;
  display: inline-block;
}

.audio-container {
  border-radius: 20px;
  padding: 12px 16px;
  min-width: 280px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.audio-container:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.dark .audio-container {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.dark .audio-container:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
}

.audio-player {
  display: flex;
  align-items: center;
  gap: 12px;
}

.audio-play-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.2);
}

.audio-play-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.audio-progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.audio-waveform {
  display: flex;
  align-items: end;
  gap: 2px;
  height: 30px;
}

.waveform-bar {
  width: 3px;
  min-height: 4px;
  border-radius: 2px;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.waveform-bar.active {
  opacity: 1;
  transform: scaleY(1.2);
}

.audio-duration {
  font-size: 0.75rem;
  font-weight: 500;
  text-align: right;
}

.audio-download-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
}

.audio-download-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.audio-caption {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.4;
}

/* Mobile optimizations for audio */
@media (max-width: 768px) {
  .audio-message {
    max-width: 90%;
  }
  
  .audio-container {
    min-width: 250px;
    padding: 10px 14px;
  }
  
  .audio-player {
    gap: 10px;
  }
  
  .audio-play-btn {
    width: 36px;
    height: 36px;
  }
  
  .audio-waveform {
    height: 25px;
  }
  
  .waveform-bar {
    width: 2.5px;
  }
  
  .audio-caption {
    font-size: 0.8125rem;
    padding: 6px 10px;
  }
}

/* Chat form improvements */
.chat-form {
  position: relative;
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .chat-form {
    margin-top: 1.5rem;
  }
}

.chat-form form {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.dark .chat-form form {
  background: rgba(23, 32, 54, 0.8);
  border-color: rgba(12, 20, 39, 0.2);
}

.chat-form form:focus-within {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.dark .chat-form form:focus-within {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

textarea {
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

@media (max-width: 768px) {
  textarea {
    font-size: 0.8125rem;
  }
}

textarea::-webkit-scrollbar {
  width: 4px;
}

textarea::-webkit-scrollbar-track {
  background: transparent;
}

textarea::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.dark textarea::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Emoji picker improvements */
.emoji-picker {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideUp 0.2s ease-out;
}

.dark .emoji-picker {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .emoji-picker {
    max-height: 350px;
    overflow-y: auto;
  }
  
  .emoji-grid {
    max-height: 200px;
  }
  
  .recording-preview,
  .selected-file-preview {
    margin-bottom: 0.5rem;
    padding: 0.75rem;
  }
}

/* Bot Feedback Button Styles */
.bot-feedback-button {
  position: relative;
}

.bot-feedback-button::after {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, #3b82f6, #2563eb);
  border-radius: 14px;
  opacity: 0;
  transition: all 0.3s;
  z-index: -1;
}

.bot-feedback-button:hover::after {
  opacity: 0.2;
  inset: -3px;
}

.bot-feedback-button:active {
  transform: scale(0.95);
}

/* Modal Animations */
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-enter-active {
  animation: modalFadeIn 0.2s ease-out;
}

.modal-leave-active {
  animation: modalFadeIn 0.2s ease-in reverse;
}

/* Bot Chat Styles */
.bot-chat {
  background: linear-gradient(135deg, rgba(255, 248, 0, 0.15), rgba(255, 235, 0, 0.25));
}

.dark .bot-chat {
  background: linear-gradient(135deg, rgba(255, 235, 0, 0.1), rgba(255, 193, 7, 0.15));
}

.bot-chat .chat-form form {
  background: rgba(255, 248, 0, 0.2);
  border: 2px solid rgba(255, 235, 0, 0.3);
  box-shadow: 0 4px 20px rgba(255, 235, 0, 0.1);
}

.dark .bot-chat .chat-form form {
  background: rgba(255, 235, 0, 0.1);
  border: 2px solid rgba(255, 235, 0, 0.2);
}

.bot-chat .chat-form form:focus-within {
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 4px 20px rgba(255, 193, 7, 0.2);
}

.dark .bot-chat .chat-form form:focus-within {
  border-color: rgba(255, 193, 7, 0.3);
  box-shadow: 0 4px 20px rgba(255, 193, 7, 0.1);
}

/* Bot Chat Send Button - Orange */
.bot-chat .modern-send-btn {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%) !important;
  color: white !important;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3) !important;
  border: none !important;
}

.bot-chat .modern-send-btn:hover {
  background: linear-gradient(135deg, #ff5722 0%, #ff9800 100%) !important;
  box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4) !important;
  transform: translateY(-2px);
}

.bot-chat .modern-send-btn:active {
  transform: scale(0.95);
}

/* Bot Message Styles - Green background for bot messages in bot chat */
.bot-chat p[class*="bg-gray-50"] {
  background: linear-gradient(135deg, #c6f6d5 0%, #68d391 100%) !important;
  color: #1a365d !important;
  border: 1px solid rgba(72, 187, 120, 0.5);
}

.dark .bot-chat p[class*="bg-gray-50"] {
  background: linear-gradient(135deg, rgba(72, 187, 120, 0.2) 0%, rgba(56, 161, 105, 0.3) 100%) !important;
  color: #9ae6b4 !important;
}

/* User messages in bot chat - Yellow background */
.bot-chat p[class*="bg-primary-500"] {
  background: linear-gradient(135deg, #fff59d 0%, #ffeb3b 100%) !important;
  color: #744210 !important;
  border: 1px solid rgba(255, 235, 59, 0.5);
}

.dark .bot-chat p[class*="bg-primary-500"] {
  background: linear-gradient(135deg, rgba(255, 235, 59, 0.2) 0%, rgba(255, 193, 7, 0.3) 100%) !important;
  color: #fef08a !important;
}

/* Bot Chat Animations */
@keyframes botChatIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.bot-chat-enter-active {
  animation: botChatIn 0.3s ease-out;
}

.bot-chat-leave-active {
  animation: botChatIn 0.3s ease-in reverse;
}

/* סגנונות לכפתורי הפעולה ללא אנימציות */
.action-button {
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  font-weight: 500;
}

/* Assign Button Styles */
.assign-button:not(.bg-blue-500):hover {
  background: rgba(59, 130, 246, 0.1) !important;
  border-color: #3b82f6 !important;
  color: #3b82f6 !important;
}

.assign-button.bg-blue-500 {
  background: #3b82f6 !important;
}

.assign-button.bg-blue-500:hover {
  background: #2563eb !important;
}

/* Bot Toggle Button Styles */
.bot-toggle-button {
  font-weight: 600;
}

.bot-toggle-button.bg-green-500 {
  background: #10b981 !important;
}

.bot-toggle-button.bg-green-500:hover {
  background: #059669 !important;
}

.bot-toggle-button.bg-red-500 {
  background: #ef4444 !important;
}

.bot-toggle-button.bg-red-500:hover {
  background: #dc2626 !important;
}

.bot-toggle-button.bg-gray-100:hover {
  background: rgba(156, 163, 175, 0.2) !important;
  border-color: #9ca3af !important;
  color: #6b7280 !important;
}

/* Enhanced Dropdown */
.assign-dropdown {
  backdrop-filter: blur(10px);
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

.dark .assign-dropdown {
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.4),
    0 10px 10px -5px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(30, 41, 59, 0.5);
}

/* Status Indicators */
.status-indicator {
  /* אנימציות הוסרו */
}

/* Icon improvements */
.material-symbols-outlined {
  font-variation-settings: 'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 20;
}

/* אנימציית הקלטה */
@keyframes recordingPulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
}

.recording-pulse {
  animation: recordingPulse 2s infinite;
}

/* אנימציית תצוגה מקדימה של קובץ */
@keyframes previewSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
}

.file-preview-enter-active {
  animation: previewSlideIn 0.3s ease-out;
}

.file-preview-leave-active {
  animation: previewSlideIn 0.3s ease-in reverse;
}

/* עיצוב חדש לתצוגה מקדימה של קובץ */
.file-preview {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.dark .file-preview {
  background: rgba(12, 20, 39, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

/* אנימציית כפתור שליחה */
.send-button {
  position: relative;
  overflow: hidden;
}

.send-button::after {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, var(--tw-gradient-from), var(--tw-gradient-to));
  opacity: 0;
  transition: all 0.3s;
  border-radius: inherit;
  z-index: -1;
}

.send-button:hover::after {
  opacity: 0.4;
  inset: -3px;
}

.send-button:active {
  transform: scale(0.95);
}

/* אנימציות למודל */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* סגנונות לכפתור הפידבק */
.feedback-button {
  transition: all 0.2s ease;
  z-index: 10;
}

/* במובייל תמיד להציג את הכפתור */
@media (max-width: 768px) {
  .feedback-button {
    opacity: 1 !important;
  }
}

/* אנימציית hover לכפתור */
.feedback-button:hover {
  transform: translateX(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark .feedback-button:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.feedback-button:active {
  transform: scale(0.95);
}



/* Send Button */
.modern-send-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 4px 16px rgba(59, 130, 246, 0.3),
    0 1px 4px rgba(0, 0, 0, 0.1);
}

.modern-send-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-2px);
  box-shadow: 
    0 8px 24px rgba(59, 130, 246, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.15);
}

.modern-send-btn:active {
  transform: scale(0.95);
}



/* Chat body flexible layout */
.chat-body {
  overflow-y: auto;
  display: flex;
  flex-direction: column-reverse;
}

/* Chat header styles to match sidebar header */
.trezo-card-header {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px 25px;
  position: relative;
  overflow: visible;
  border-radius: 6px 6px 0 0;
  margin: -15px -20px 0 -20px;
}

@media (min-width: 768px) {
  .trezo-card-header {
    margin: -20px -20px 0 -20px;
  }
}

.dark .trezo-card-header {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.9) 0%, 
    rgba(30, 58, 138, 0.3) 50%, 
    rgba(37, 99, 235, 0.2) 100%);
  border-color: rgba(59, 130, 246, 0.2);
  border-bottom-color: rgba(59, 130, 246, 0.2);
}

.trezo-card-header::before {
  display: none;
}

/* Dark mode shadows */

/* Enhanced Chat Panel Styles */
.chat-panel-enhanced {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.5) !important;
  border-radius: 16px !important;
}

.dark .chat-panel-enhanced {
  background: rgba(15, 23, 42, 0.9) !important;
  border-color: rgba(30, 41, 59, 0.5) !important;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(30, 41, 59, 0.8) !important;
}

/* Enhanced Chat Header */
.trezo-card-header {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-bottom: 1px solid #e5e7eb !important;
  padding: 20px 25px !important;
  position: relative !important;
  overflow: visible !important;
  border-radius: 16px 16px 0 0 !important;
  margin: -15px -20px 0 -20px !important;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

@media (min-width: 768px) {
  .trezo-card-header {
    margin: -20px -20px 0 -20px !important;
  }
}

.dark .trezo-card-header {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.95) 0%, 
    rgba(30, 41, 59, 0.9) 30%,
    rgba(51, 65, 85, 0.85) 100%) !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
  border-bottom-color: rgba(59, 130, 246, 0.25) !important;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.3),
    0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.trezo-card-header::before {
  display: none;
}

/* Enhanced Chat Title */
.trezo-card-title {
  color: #1f2937;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.dark .trezo-card-title {
  background: linear-gradient(135deg, #f9fafb 0%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Enhanced Chat Form */
.chat-form form {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(15px) !important;
  border: 1px solid rgba(59, 130, 246, 0.2) !important;
  box-shadow: 
    0 8px 32px rgba(59, 130, 246, 0.15),
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
  border-radius: 16px !important;
}

.dark .chat-form form {
  background: rgba(15, 23, 42, 0.9) !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
  box-shadow: 
    0 8px 32px rgba(59, 130, 246, 0.25),
    0 2px 8px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(30, 41, 59, 0.8) !important;
}

.chat-form form:focus-within {
  border-color: rgba(59, 130, 246, 0.5) !important;
  box-shadow: 
    0 8px 32px rgba(59, 130, 246, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.1),
    0 0 0 3px rgba(59, 130, 246, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
}

.dark .chat-form form:focus-within {
  border-color: rgba(59, 130, 246, 0.6) !important;
  box-shadow: 
    0 8px 32px rgba(59, 130, 246, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.4),
    0 0 0 3px rgba(59, 130, 246, 0.2),
    inset 0 1px 0 rgba(30, 41, 59, 0.8) !important;
}

/* Enhanced Textarea */
.chat-form textarea {
  background: transparent !important;
  border: none !important;
  color: #1f2937 !important;
}

.dark .chat-form textarea {
  color: #f9fafb !important;
}

.chat-form textarea::placeholder {
  color: #6b7280 !important;
}

.dark .chat-form textarea::placeholder {
  color: #9ca3af !important;
}

/* Enhanced Messages */
.chat-body ul li div div p {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* Our messages (right side) */
.chat-body ul li div div p.bg-primary-500 {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 
    0 4px 16px rgba(59, 130, 246, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Other messages (left side) */
.chat-body ul li div div p.bg-gray-50 {
  background: rgba(248, 250, 252, 0.9) !important;
  color: #1f2937 !important;
  border-color: rgba(59, 130, 246, 0.1);
  box-shadow: 
    0 4px 16px rgba(59, 130, 246, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
}

.dark .chat-body ul li div div p.bg-gray-50 {
  background: rgba(30, 41, 59, 0.9) !important;
  color: #f1f5f9 !important;
  border-color: rgba(59, 130, 246, 0.2);
  box-shadow: 
    0 4px 16px rgba(59, 130, 246, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Enhanced Chat Body Scrollbar */
.chat-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(59, 130, 246, 0.3) transparent;
}

.chat-body::-webkit-scrollbar {
  width: 6px;
}

.chat-body::-webkit-scrollbar-track {
  background: transparent;
}

.chat-body::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(99, 102, 241, 0.2) 100%);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark .chat-body::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(99, 102, 241, 0.4) 100%);
  border-color: rgba(0, 0, 0, 0.1);
}

.chat-body::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(99, 102, 241, 0.4) 100%);
}

/* Removed global overflow styles - should be handled at app level */

/* Mobile layout for action buttons */
@media (max-width: 768px) {
  /* הפוך את הכותרת לפריסה של שורה אחת */
  .trezo-card-header {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
  }
  
  /* הכותרת הראשית תתכווץ כדי לפנות מקום */
  .trezo-card-title {
    flex: 1 !important;
    min-width: 0 !important;
  }
  
  /* הכפתורים יהיו בצד הנגדי של כפתור החזרה */
  .trezo-card-subtitle {
    margin-top: 0 !important;
    position: static !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
    flex-shrink: 0 !important;
  }
  
  /* כפתור הבוט למעלה */
  .bot-toggle-button {
    order: 1 !important;
    width: 36px !important;
    height: 36px !important;
  }
  
  /* כפתור השיוך למטה */
  .assign-button {
    order: 2 !important;
    width: 36px !important;
    height: 36px !important;
  }
  
  /* קצוות מרובעים לצ'אט במובייל */
  .chat-panel-enhanced {
    border-radius: 0 !important;
  }
  
  .trezo-card-header {
    border-radius: 0 !important;
    padding: 6px 16px !important;
    margin: -15px -15px 0 -15px !important;
  }
  
  /* הקטן את התמונה והטקסט */
  .trezo-card-title img {
    width: 50px !important;
    height: 50px !important;
  }
  
  .trezo-card-title .font-semibold {
    font-size: 16px !important;
  }
  
  .trezo-card-title span:not(.font-semibold) {
    font-size: 12px !important;
  }
  
  /* וודא שפופאפ השיוך יופיע במובייל */
  .assign-dropdown {
    z-index: 999999999 !important;
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90vw !important;
    max-width: 320px !important;
  }
}
</style>

